import type { Contact } from '../../types/contact';
import { NO_CHANNELS_REASON } from '../../lib/platformEnabled';

// Ad Preview Stage 3 §3.1. Client-side eligibility preview for the
// single-send modal -- mirrors the two gates the server enforces so
// the AM sees who'll receive before clicking Send:
//   1. The contact's AccountLink for THIS location must opt in to
//      campaign previews (receivesCampaignPreviews).
//   2. Per channel: email needs channelEmail + a non-empty email;
//      SMS needs channelSMS + a non-empty phone.
//
// Pure -- no IO. The server re-checks everything; this is purely the
// modal's preview. Extracted from the modal so it can be unit-tested.

export type ContactEligibility = {
  contact: Contact;
  emailAvailable: boolean;
  smsAvailable: boolean;
};

export type RowSendEligibility = {
  eligible: ContactEligibility[];
  excluded: Array<{ contact: Contact; reason: string }>;
  // Set when the ROW cannot be sent at all (no ad channel is on for this
  // location). Every contact is then excluded with the same reason.
  rowExcludedReason: string | null;
};

export function computeRowSendEligibility(
  contacts: Contact[],
  locationAccountId: string,
  // The row's effective channels (campaign AND account AND row override), when
  // the caller knows them. Neither on = the preview has nothing to review, so
  // the row is not sendable. The server re-checks with the same gate.
  rowChannels?: { meta: boolean; google: boolean },
): RowSendEligibility {
  const eligible: ContactEligibility[] = [];
  const excluded: Array<{ contact: Contact; reason: string }> = [];
  if (rowChannels && !rowChannels.meta && !rowChannels.google) {
    for (const c of contacts) excluded.push({ contact: c, reason: NO_CHANNELS_REASON });
    return { eligible, excluded, rowExcludedReason: NO_CHANNELS_REASON };
  }
  for (const c of contacts) {
    const link = c.accountLinks.find((l) => l.accountId === locationAccountId);
    // Not linked to this location -- shouldn't happen given the query
    // is scoped by accountId, but skip silently if it does.
    if (!link) continue;
    if (!link.receivesCampaignPreviews) {
      excluded.push({ contact: c, reason: 'Campaign previews not enabled' });
      continue;
    }
    const emailAvailable = c.channelEmail && !!c.email;
    const smsAvailable = c.channelSMS && !!c.phone;
    if (!emailAvailable && !smsAvailable) {
      excluded.push({ contact: c, reason: 'No email/SMS channel enabled' });
      continue;
    }
    eligible.push({ contact: c, emailAvailable, smsAvailable });
  }
  return { eligible, excluded, rowExcludedReason: null };
}
